/**
 * Panel showing posts for a selected segment, with links to individual posts.
 */
import { Link } from 'react-router-dom';
import { CloseIcon } from '../ui/icons';
import type { Segment, MapPost } from '../../types/map';

interface SegmentPanelProps {
  segment: Segment;
  posts: MapPost[];
  onClose: () => void;
}

export function SegmentPanel({ segment, posts, onClose }: SegmentPanelProps) {
  return (
    <div className="absolute bottom-20 lg:bottom-4 left-4 right-4 lg:left-auto lg:right-4 lg:w-96 z-20">
      <div className="card max-h-80 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">{segment.displayName}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {segment.waterwayName} • {Number(segment.postCount)} {Number(segment.postCount) === 1 ? 'post' : 'posts'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {posts.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-slate-500 dark:text-slate-400 mb-4">No posts at this location yet</p>
              <Link to={`/create?segment=${segment.id}`} className="btn btn-primary">
                Be the first to post
              </Link>
            </div>
          ) : (
            Array.isArray(posts) && posts.map((post) => (
              <Link
                key={post.id}
                to={`/post/${post.id}`}
                className="block p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-slate-900 dark:text-white text-sm">
                    {post.author?.displayName || 'Unknown'}
                  </span>
                  <span className="text-slate-400 text-xs">@{post.author?.handle || 'unknown'}</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
                  {post.content}
                </p>
                {Array.isArray(post.media) && post.media.length > 0 && (
                  <div className="mt-2 flex gap-1">
                    {post.media.slice(0, 3).map((m) => (
                      <div key={m.url} className="w-12 h-12 rounded bg-slate-200 dark:bg-slate-600 overflow-hidden">
                        <img src={m.thumbnailUrl || m.url} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                    {post.media.length > 3 && (
                      <div className="w-12 h-12 rounded bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-sm text-slate-500">
                        +{post.media.length - 3}
                      </div>
                    )}
                  </div>
                )}
              </Link>
            ))
          )}
        </div>

        <div className="p-4 border-t border-slate-100 dark:border-slate-700">
          <Link to={`/create?segment=${segment.id}`} className="btn btn-primary w-full">
            Post here
          </Link>
        </div>
      </div>
    </div>
  );
}
